<template>
  <a
    role="button"
    class="gallery-item-thumb"
    :style="{ height, width }"
    :class="{ selected: isSelected, assigned: isAssigned }"
    :title="title"
    @mouseover="isHovering = true"
    @mouseleave="isHovering = false"
  >
    <!-- Media Visibility Indicators for RLS / Website Medias -->
    <div
      v-show="[visibilitySettings.showRlsListings, visibilitySettings.showWebsites].includes(false)"
      class="media-visibility-container shadow d-flex"
    >
      <div v-show="!visibilitySettings.showWebsites" class="media-visibility-col">
        <img
          src="/img/icon-hide-websites.svg"
          alt="hidden_websites"
          :title="`This item is hidden from your website(s)`"
        />
      </div>
      <div v-show="!visibilitySettings.showRlsListings" class="media-visibility-col">
        <img
          src="/img/icon-hide-rls-listings.svg"
          alt="hidden_rls_listings"
          :title="`This item is hidden from your RLS listings`"
        />
      </div>
    </div>

    <!-- If it's an image -->
    <img
      v-if="isImage"
      :src="getUrl"
      draggable="false"
      class="rs-gallery-item-updated-img rs-rounded img-selected"
      @click="handleClick"
    >
    <!-- If it is not an image -->
    <div
      v-else
      class="rs-rounded rs-file-thumb text-truncate "
      @click="handleClick"
    >
      <div class="text-center mb-1 thumb-icon">
        <i class="fa fa-file" />
      </div>
      <div class="text-center overflow-hidden ">
        <small
          class="d-block text-truncate"
          :style="{ maxWidth: `${parseFloat(width) - 2.5}rem` }"
        >
          {{ fileDisplayName }}
        </small>
        <small
          v-if="subFileDisplayName"
          class="d-block text-truncate"
          :style="{ maxWidth: `${parseFloat(width) - 3.5}rem`, fontSize: '11px' }"
        >
          {{ (subFileDisplayName.length > 18 ? subFileDisplayName.substr(0, 18) + '...' : subFileDisplayName).toLowerCase() }}
        </small>
      </div>
    </div>

    <div
      v-if="isDisabled"
      class="disabled rs-rounded h-100 cursor-pointer"
      @click="handleClick"
    />

    <!-- Drag icon -->
    <div
      v-show="!isDisabled && !isChecked && isDrag && isUpdatePermitted"
      class="drag-icon"
    >
      <RsIcon
        weight="fas"
        icon="grip-vertical"
      />
    </div>
    <!-- Featured / Alternate Featured badge -->
    <div
      v-if="isFeatured || isAlternateFeatured"
      :style="isHovering ? 'height:45%' : 'height:25%'"
      class="featured"
    >
      <RsBadge
        pill
        variant="dark"
      >{{ isFeatured ? 'Featured Image' : 'Alternate Featured Image' }}</RsBadge>
    </div>

    <!-- Promotional image indicator -->
    <div
      v-if="isPromotional"
      class="promotional-indicator"
      title="This image has been detected as promotional content"
    >
      <RsIcon
        weight="fas"
        icon="exclamation-triangle"
      />
    </div>

    <RsFormCheckbox
      v-if="isUpdatePermitted"
      :id="`checkbox_${getUrl}_${data?.id}`"
      class="assigned-checkbox"
      :name="`checkbox_${getUrl}_${data?.id}`"
      :model-value="isChecked"
      @change="$emit('on-checkbox-changed', $event, data);"
    />

    <div
      v-if="(!isChecked && showOverlay && isUpdatePermitted) && (isAssignedSwitchVisible || isFeaturedCheckVisible)"
      class="overlay-box"
    >
      <RsFormSwitch
        :id="`switch_${getUrl}_${data?.id}`"
        :model-value="isAssigned"
        :disabled="isFeatured || isSaving"
        :title="isFeatured ? 'You cannot disable a featured photo' : `${assignText}`"
        class="assigned-switch"
        @change="$emit('on-toggle-assigned', $event, data)"
      />

      <RsFavorite
        v-if="!isDisabled && isFeaturedCheckVisible"
        icon="star"
        :value="isFeatured"
        class="assigned-favorite"
        @input="$emit('on-favorite-changed', $event, data);"
      />
    </div>
  </a>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

export default defineComponent({
  name: 'RsGalleryItemUpdated',

  props: {
    url: {
      type: String,
    },
    title: {
      type: String,
    },
    fileDisplayName: {
      type: String,
      default: '',
    },
    subFileDisplayName: {
      type: String,
      default: '',
    },
    data: {
      type: Object,
    },
    isImage: {
      type: Boolean,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    isAssigned: {
      type: Boolean,
      default: false,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    isAssignedSwitchVisible: {
      type: Boolean,
      default: false,
    },
    isFeatured: {
      type: Boolean,
      default: false,
    },
    isFeaturedCheckVisible: {
      type: Boolean,
      default: false,
    },
    isPromotional: {
      type: Boolean,
      default: false,
    },
    isAlternateFeatured: {
      type: Boolean,
      default: false,
    },
    isChecked: {
      type: Boolean,
      default: false,
    },
    isDrag: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: '9em',
    },
    width: {
      type: String,
      default: '9em',
    },
    useXhr: {
      type: Boolean,
      default: false,
    },
    imageRenderKey: {
      type: String,
      default: '',
    },
    showOverlay: {
      type: Boolean,
      default: true,
    },
    preventQueryString: {
      type: Boolean,
    },

    visibilitySettings: {
      type: Object as PropType<{ showWebsites: boolean, showRlsListings: boolean }>,
      default: () => ({ showWebsites: true, showRlsListings: true }),
    },
  },

  emits: ['click', 'on-checkbox-changed', 'on-favorite-changed', 'on-toggle-assigned'],

  data() {
    return {
      isHovering: false,
    };
  },

  computed: {
    getUrl() {
      if (this.preventQueryString) {
        return this.url;
      }

      let querystring = '?';

      if (this.useXhr) {
        querystring += '&x-request=xhr';
      }

      if (this.imageRenderKey) {
        querystring += `&key=${this.imageRenderKey}`;
      }

      if (querystring === '?') {
        querystring = '';
      }

      return `${this.url}${querystring.replace('?&', '?')}`;
    },

    assignText(): string {
      return this.isAssigned
        ? this.$ts('gallery.tooltips.disableAssign')
        : this.$ts('gallery.tooltips.enableAssign');
    },
  },

  methods: {
    handleClick() {
      this.$emit('click', this.data);
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/styles/_variables';
@import '@/styles/media';
.gallery-item-thumb {
  & > .disabled {
    background-color: rgba(0,0,0,0.45) !important;
  }

  .featured {
    left: 0;
    right: 0;
    width: 100%;
  }
}

.promotional-indicator {
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: #ffc107e6;
  color: #856404;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  z-index: 2;
  pointer-events: none;
}
</style>
